// frontend/src/components/SupplierLayout.js  (replacement)
// Matches WorkshopLayout: the chrome/ink page ground, a top bar carrying the
// same language / notification / profile controls, and the full-bleed rule for
// pages that own their own width. Was a slate gradient with no top bar, so the
// supplier space felt like a different application.

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Outlet, useLocation } from 'react-router-dom';
import SupplierDecodedSidebar from './SupplierDecodedSidebar';
import NotificationBell from './NotificationBell';
import ProfileMenu from './ProfileMenu';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';

// Pages converted to the chrome/ink design own their max-width and padding
// (via components/ui/Iparc <Page>), so they render edge-to-edge against the
// sidebar. Anything not yet converted keeps the original gutter. Add a route
// here when its page is converted.
const FULL_BLEED = ['/supplier', '/supplier/quotes', '/supplier/invoices', '/supplier/receipts'];

// The invoice detail route carries an id, so it can never be in the list above.
const isConverted = (p) => FULL_BLEED.includes(p) || p.startsWith('/supplier/invoices/');

const SupplierLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [badges, setBadges] = useState({ availableRfqs: 0, unpaidInvoices: 0, pendingCounters: 0 });
    const { token, logout, user } = useAuth();
    const { pathname } = useLocation();
    const isFullBleed = isConverted(pathname);

    // Sidebar badges are lifted here so the sidebar and the dashboard read the
    // same numbers — otherwise the badges sit at 0 and contradict the page.
    useEffect(() => {
        if (!token) return;
        const h = { headers: { Authorization: `Bearer ${token}` } };
        Promise.all([
            axios.get(`${API_URL}/quotations/supplier/stats`, h).catch(() => null),
            axios.get(`${API_URL}/quotations/supplier-invoices/supplier`, h).catch(() => null),
            axios.get(`${API_URL}/spare-parts/responses`, h).catch(() => null),
        ]).then(([s, i, q]) => {
            // The endpoint has returned a bare array in some versions and
            // { data: [...] } in others — accept either rather than showing 0.
            const raw = i?.data;
            const invoices = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
            const quotes = Array.isArray(q?.data) ? q.data : [];
            setBadges({
                availableRfqs: Number(s?.data?.availableRfqs) || 0,
                unpaidInvoices: invoices.filter((inv) => inv?.status === 'awaiting_payment').length,
                // A buyer's counter-offer sitting unanswered — the one thing
                // here that costs money to leave alone.
                pendingCounters: quotes.filter((r) => r?.status === 'client_countered').length,
            });
        });
    }, [token, pathname]);

    useEffect(() => {
        if (!token) return;
        const fetchNotifs = () =>
            axios
                .get(`${API_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
                .then((res) => setNotifications(res.data))
                .catch(() => { });
        fetchNotifs();
        const interval = setInterval(fetchNotifs, 15000);
        return () => clearInterval(interval);
    }, [token]);

    return (
        <div className="flex w-full min-h-screen bg-[#0D0C0B] text-[#F4F1EA] font-sans">
            {/* Desktop Sidebar */}
            <div className="hidden sm:block">
                <SupplierDecodedSidebar {...badges} onLogout={logout} />
            </div>

            {/* Mobile Sidebar (slide-over) */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 flex sm:hidden">
                    <div className="relative w-[248px] bg-[#100F0D]">
                        <button
                            className="absolute top-2 right-2 p-2 text-[#8F877B] hover:text-[#F4F1EA] z-10"
                            onClick={() => setSidebarOpen(false)}
                            aria-label="Fermer le menu"
                        >
                            ✕
                        </button>
                        <SupplierDecodedSidebar
                            {...badges}
                            onLogout={logout}
                            onClose={() => setSidebarOpen(false)}
                        />
                    </div>
                    <div
                        className="flex-1 bg-black/50"
                        onClick={() => setSidebarOpen(false)}
                    />
                </div>
            )}

            {/* Main content area */}
            <main className={`flex-1 min-w-0 overflow-y-auto ${isFullBleed ? 'p-0' : 'p-4 sm:p-6'}`}>
                <div className={`flex items-center justify-between mb-4 ${isFullBleed ? 'px-[30px] pt-6' : ''}`}>
                    <div className="sm:hidden flex items-center">
                        <button
                            className="p-2 rounded-[9px] border border-[#2B2724] bg-[#141210] text-[#C9C3B6]"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Ouvrir le menu"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <span className="ml-3 font-['Space_Grotesk',sans-serif] text-[15px] font-semibold">
                            Fournisseur
                        </span>
                    </div>
                    {/* Spacer for desktop (hamburger hidden) */}
                    <div className="hidden sm:block" />
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher />
                        <NotificationBell
                            notifications={notifications}
                            onRefresh={() =>
                                axios
                                    .get(`${API_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
                                    .then((res) => setNotifications(res.data))
                                    .catch(() => { })
                            }
                        />
                        <ProfileMenu user={user} />
                    </div>
                </div>
                <Outlet />
            </main>
        </div>
    );
};

export default SupplierLayout;
